import React, { useState, useEffect, useCallback } from "react";
import { SvgIcon } from "../SvgIcon";
import { apiGet, apiPost, apiPut, apiDelete } from "../../api";
import { t, Locale } from "../../i18n";

interface MailboxInfo {
  id: number;
  email: string;
  localpart: string;
  quota_bytes: number;
  enabled: boolean;
  created_at: string;
}

interface AliasInfo {
  source_email: string;
  dest_email: string;
  enabled: boolean;
}

interface Identity {
  id: string;
  emailAddress: string;
  displayName: string;
  avatarColor: string;
  identityType: "primary" | "alias" | "custom_domain";
  deliversTo?: string;
  domainVerifiedAt?: string;
  isActive: boolean;
}

interface EditingIdentity extends Identity {
  fromPreview: string;
}

type AddingType = null | "alias" | "custom_domain";
type CustomDomainStep = "enter" | "verify" | "setup" | null;

const AVATAR_COLORS = [
  "#60a5fa", // blue
  "#34d399", // green
  "#fbbf24", // amber
  "#f87171", // red
  "#a78bfa", // purple
  "#fb7185", // pink
];

function getInitials(displayName: string): string {
  return displayName
    .split(" ")
    .map(word => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function IdentitiesAndAliases(props: { locale: Locale }) {
  const locale = props.locale;
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingIdentity | null>(null);
  const [adding, setAdding] = useState<AddingType>(null);
  const [domainStep, setDomainStep] = useState<CustomDomainStep>(null);
  const [customHexColor, setCustomHexColor] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<string | null>(null);
  const [aliasForm, setAliasForm] = useState({ local: "", domain: "kveja.com", displayName: "", customAvatar: false, avatarColor: "#60a5fa" });
  const [domainForm, setDomainForm] = useState({ domain: "", verificationToken: "", dnsRecorded: false });
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [aliasSaving, setAliasSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const mbRes = await apiGet<{ mailboxes: MailboxInfo[]; aliases: AliasInfo[] }>("/v1/mailboxes");

      if (mbRes.ok) {
        const { mailboxes, aliases } = mbRes.data;

        // Build identities from API data
        const ids: Identity[] = [];

        // Primary mailbox — first enabled kveja.com mailbox
        const primaryMb = mailboxes.find((m) => m.email.endsWith("@kveja.com") && m.enabled);
        if (primaryMb) {
          ids.push({
            id: `mb-${primaryMb.id}`,
            emailAddress: primaryMb.email,
            displayName: primaryMb.localpart,
            avatarColor: "#3B82F6",
            identityType: "primary",
            isActive: true
          });
        }

        // Aliases
        for (const alias of aliases) {
          if (!alias.enabled) continue;
          ids.push({
            id: `alias-${alias.source_email}`,
            emailAddress: alias.source_email,
            displayName: alias.source_email.split("@")[0],
            avatarColor: "#3B82F6",
            identityType: "alias",
            deliversTo: alias.dest_email,
            isActive: true
          });
        }

        // Custom domain mailboxes
        for (const mb of mailboxes) {
          if (!mb.enabled) continue;
          if (mb.email.endsWith("@kveja.com")) continue; // already handled as primary
          ids.push({
            id: `mb-${mb.id}`,
            emailAddress: mb.email,
            displayName: mb.localpart,
            avatarColor: "#10B981",
            identityType: "custom_domain",
            isActive: true
          });
        }

        setIdentities(ids);
      } else {
        setError(mbRes.error);
      }
    } catch {
      setError("Failed to load mailbox data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditStart = (identity: Identity) => {
    setEditing({
      ...identity,
      fromPreview: `${identity.displayName} <${identity.emailAddress}>`
    });
  };

  const handleEditChange = (field: keyof Omit<EditingIdentity, "fromPreview">, value: any) => {
    if (!editing) return;
    const updated = { ...editing, [field]: value };
    const fromPreview = `${updated.displayName} <${updated.emailAddress}>`;
    setEditing({ ...updated, fromPreview });
  };

  const handleEditSave = () => {
    if (!editing) return;
    const { fromPreview, ...identityData } = editing;
    setIdentities(identities.map(id => id.id === identityData.id ? identityData : id));
    setEditing(null);
  };

  const handleRemoveClick = (id: string) => {
    const identity = identities.find(i => i.id === id);
    if (identity?.identityType === "primary") return;
    setRemoveConfirm(id);
  };

  const handleRemoveConfirm = async () => {
    if (!removeConfirm) return;
    const identity = identities.find(i => i.id === removeConfirm);
    if (!identity) return;

    const res = await apiDelete(`/v1/mailboxes/aliases/${encodeURIComponent(identity.emailAddress)}`);
    if (res.ok) {
      setRemoveConfirm(null);
      await fetchData();
    }
  };

  const handleAddAlias = async () => {
    if (!aliasForm.local.trim()) return;
    setAliasSaving(true);
    setAliasError(null);

    const res = await apiPost<{ ok: boolean }>("/v1/mailboxes/aliases", {
      local_part: aliasForm.local.trim()
    });

    if (res.ok) {
      setAdding(null);
      setAliasForm({ local: "", domain: "kveja.com", displayName: "", customAvatar: false, avatarColor: "#60a5fa" });
      await fetchData();
    } else {
      setAliasError(res.error);
    }
    setAliasSaving(false);
  };

  const handleAddDomainStep1 = () => {
    if (!domainForm.domain.trim()) return;
    const token = "kveja-verify=" + crypto.randomUUID().replace(/-/g, "").slice(0, 16);
    setDomainForm(prev => ({ ...prev, verificationToken: token }));
    setDomainStep("verify");
  };

  const handleDomainVerified = () => {
    setDomainStep("setup");
  };

  const handleDomainComplete = () => {
    setAdding(null);
    setDomainStep(null);
    setDomainForm({ domain: "", verificationToken: "", dnsRecorded: false });
    fetchData();
  };

  if (loading) {
    return (
      <div>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "4px" }}>{t(locale, "identities.title")}</h3>
        <p style={{ fontSize: "13px", color: "var(--muted)" }}>{t(locale, "common.loading")}</p>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "4px" }}>{t(locale, "identities.title")}</h3>
      <p style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px" }}>
        {t(locale, "identities.description")}
      </p>

      {error && (
        <div style={{ padding: "10px 12px", marginBottom: "12px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "6px", fontSize: "12px", color: "#ef4444" }}>
          {error}
        </div>
      )}

      {/* Identities List */}
      <div style={{ marginBottom: "16px" }}>
        {identities.map(identity => (
          <div key={identity.id}>
            {/* Identity Row */}
            {editing?.id !== identity.id && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px",
                  border: "1px solid var(--line)",
                  borderRadius: "6px",
                  marginBottom: "8px",
                  background: "var(--surface)",
                  opacity: identity.isActive ? 1 : 0.6
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: identity.avatarColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "white",
                      flexShrink: 0
                    }}
                  >
                    {getInitials(identity.displayName)}
                  </div>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>
                      {identity.emailAddress}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "2px" }}>
                      {identity.displayName}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "2px" }}>
                      {identity.identityType === "primary" && t(locale, "identities.primaryAddress")}
                      {identity.identityType === "alias" && (
                        <>Alias <span style={{ display: "inline-flex", width: 12, height: 12 }}><SvgIcon name="arrow-right" title="" /></span> {identity.deliversTo}</>
                      )}
                      {identity.identityType === "custom_domain" && t(locale, "identities.customDomain")}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => handleEditStart(identity)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--accent)",
                      cursor: "pointer",
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                    title="Edit identity"
                  >
                    <SvgIcon name="edit" className="" />
                  </button>
                  {identity.identityType !== "primary" && (
                    <button
                      onClick={() => handleRemoveClick(identity.id)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#ef4444",
                        cursor: "pointer",
                        padding: "4px 8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                      title="Delete identity"
                    >
                      <SvgIcon name="x-circle" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Edit Form */}
            {editing?.id === identity.id && (
              <div style={{ padding: "16px", border: "1px solid var(--accent)", borderRadius: "6px", marginBottom: "8px", background: "var(--surface)" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "12px" }}>
                  {t(locale, "identities.editing", { email: editing.emailAddress })}
                </div>

                {/* Avatar */}
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", marginBottom: "8px" }}>{t(locale, "identities.avatar")}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background: editing.avatarColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "white"
                      }}
                    >
                      {getInitials(editing.displayName)}
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {AVATAR_COLORS.map(color => (
                        <button
                          key={color}
                          onClick={() => handleEditChange("avatarColor", color)}
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background: color,
                            border: editing.avatarColor === color ? "2px solid var(--text)" : "1px solid transparent",
                            cursor: "pointer"
                          }}
                        />
                      ))}
                      <button
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          border: "1px solid var(--control-border)",
                          background: "transparent",
                          cursor: "pointer",
                          fontSize: "16px"
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {showColorPicker && (
                    <div style={{ marginTop: "8px" }}>
                      <input
                        type="text"
                        value={customHexColor}
                        onChange={(e) => setCustomHexColor(e.target.value)}
                        placeholder="#000000"
                        style={{
                          width: "100px",
                          padding: "6px 8px",
                          border: "1px solid var(--control-border)",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontFamily: "monospace"
                        }}
                        onBlur={() => {
                          if (/^#[0-9a-fA-F]{6}$/.test(customHexColor)) {
                            handleEditChange("avatarColor", customHexColor);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Display Name */}
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>
                    {t(locale, "identities.displayName")}
                  </label>
                  <input
                    type="text"
                    value={editing.displayName}
                    onChange={(e) => handleEditChange("displayName", e.target.value)}
                    maxLength={100}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      border: "1px solid var(--control-border)",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontFamily: "inherit",
                      color: "var(--text)",
                      background: "var(--surface)",
                      boxSizing: "border-box"
                    }}
                  />
                  <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "4px" }}>
                    {t(locale, "identities.displayName.hint")}
                  </div>
                </div>

                {/* From Preview */}
                <div style={{ marginBottom: "16px", padding: "8px", background: "var(--canvas)", borderRadius: "4px", fontFamily: "monospace", fontSize: "12px", color: "var(--text)" }}>
                  From: {editing.fromPreview}
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={handleEditSave}
                    style={{
                      background: "var(--accent)",
                      color: "white",
                      border: "none",
                      padding: "6px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 600
                    }}
                  >
                    {t(locale, "common.save")}
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    style={{
                      background: "transparent",
                      color: "var(--text)",
                      border: "1px solid var(--control-border)",
                      padding: "6px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    {t(locale, "common.cancel")}
                  </button>
                </div>
              </div>
            )}

            {/* Remove Confirmation */}
            {removeConfirm === identity.id && (
              <div style={{ padding: "12px", marginBottom: "8px", background: "#ef4444", color: "white", borderRadius: "6px", fontSize: "12px" }}>
                <div style={{ marginBottom: "8px" }}>
                  {t(locale, "identities.removeConfirm", { email: identity.emailAddress })}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={handleRemoveConfirm}
                    style={{
                      background: "white",
                      color: "#ef4444",
                      border: "none",
                      padding: "4px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: 600
                    }}
                  >
                    {t(locale, "common.remove")}
                  </button>
                  <button
                    onClick={() => setRemoveConfirm(null)}
                    style={{
                      background: "transparent",
                      color: "white",
                      border: "1px solid white",
                      padding: "4px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "11px"
                    }}
                  >
                    {t(locale, "common.cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <button
          onClick={() => setAdding("alias")}
          style={{
            background: "transparent",
            border: "1px solid var(--accent)",
            color: "var(--accent)",
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: 500
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(96, 165, 250, 0.1)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {t(locale, "identities.addAlias")}
        </button>
      </div>

      {/* Add Alias Form */}
      {adding === "alias" && (
        <div style={{ padding: "16px", border: "1px solid var(--accent)", borderRadius: "6px", marginBottom: "16px", background: "var(--surface)" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "12px" }}>{t(locale, "identities.alias.title")}</div>

          {aliasError && (
            <div style={{ padding: "8px 10px", marginBottom: "12px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "4px", fontSize: "12px", color: "#ef4444" }}>
              {aliasError}
            </div>
          )}

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>{t(locale, "identities.alias.address")}</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                value={aliasForm.local}
                onChange={(e) => setAliasForm(prev => ({ ...prev, local: e.target.value }))}
                placeholder="oskar.berg"
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  border: "1px solid var(--control-border)",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontFamily: "inherit",
                  color: "var(--text)",
                  background: "var(--surface)",
                  boxSizing: "border-box"
                }}
              />
              <div style={{ padding: "8px 10px", background: "var(--canvas)", borderRadius: "4px", fontSize: "12px", color: "var(--text)", whiteSpace: "nowrap" }}>
                @{aliasForm.domain}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>{t(locale, "identities.alias.deliverTo")}</label>
            <select
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box"
              }}
            >
              {identities
                .filter(id => id.identityType === "primary" || id.identityType === "custom_domain")
                .map(id => (
                  <option key={id.id} value={id.emailAddress}>{id.emailAddress}</option>
                ))}
            </select>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>{t(locale, "identities.displayName")}</label>
            <input
              type="text"
              value={aliasForm.displayName}
              onChange={(e) => setAliasForm(prev => ({ ...prev, displayName: e.target.value }))}
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box"
              }}
            />
          </div>

          <div style={{ marginBottom: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "12px" }}>
              <input type="radio" checked={!aliasForm.customAvatar} onChange={() => setAliasForm(prev => ({ ...prev, customAvatar: false }))} />
              {t(locale, "identities.alias.sameAvatar")}
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "12px" }}>
              <input type="radio" checked={aliasForm.customAvatar} onChange={() => setAliasForm(prev => ({ ...prev, customAvatar: true }))} />
              {t(locale, "identities.alias.customAvatar")}
            </label>
          </div>

          {aliasForm.customAvatar && (
            <div style={{ marginBottom: "12px" }}>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {AVATAR_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setAliasForm(prev => ({ ...prev, avatarColor: color }))}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: color,
                      border: aliasForm.avatarColor === color ? "2px solid var(--text)" : "1px solid transparent",
                      cursor: "pointer"
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: "16px", padding: "8px", background: "var(--canvas)", borderRadius: "4px", fontFamily: "monospace", fontSize: "12px", color: "var(--text)" }}>
            From: {aliasForm.displayName} &lt;{aliasForm.local}@{aliasForm.domain}&gt;
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleAddAlias}
              disabled={aliasSaving}
              style={{
                background: "var(--accent)",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: aliasSaving ? "wait" : "pointer",
                fontSize: "12px",
                fontWeight: 600,
                opacity: aliasSaving ? 0.7 : 1
              }}
            >
              {aliasSaving ? t(locale, "identities.alias.adding") : t(locale, "identities.alias.btn")}
            </button>
            <button
              onClick={() => { setAdding(null); setAliasError(null); }}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--control-border)",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Add Custom Domain Form */}
      {adding === "custom_domain" && domainStep === "enter" && (
        <div style={{ padding: "16px", border: "1px solid var(--accent)", borderRadius: "6px", marginBottom: "16px", background: "var(--surface)" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "12px" }}>{t(locale, "identities.customDomain.add")}</div>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>{t(locale, "identities.customDomain.domain")}</label>
            <input
              type="text"
              value={domainForm.domain}
              onChange={(e) => setDomainForm(prev => ({ ...prev, domain: e.target.value }))}
              placeholder="mycompany.com"
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box"
              }}
            />
            <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "4px" }}>{t(locale, "identities.customDomain.domainHint")}</div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleAddDomainStep1}
              style={{
                background: "var(--accent)",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600
              }}
            >
              {t(locale, "common.continue")}
            </button>
            <button
              onClick={() => { setAdding(null); setDomainStep(null); }}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--control-border)",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Domain Verification Step */}
      {adding === "custom_domain" && domainStep === "verify" && (
        <div style={{ padding: "16px", border: "1px solid var(--accent)", borderRadius: "6px", marginBottom: "16px", background: "var(--surface)" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "12px" }}>
            {t(locale, "identities.customDomain.verify")}
          </div>
          <div style={{ fontSize: "12px", color: "var(--text)", marginBottom: "12px" }}>
            {domainForm.domain}
          </div>

          <div style={{ marginBottom: "16px", padding: "12px", background: "var(--canvas)", borderRadius: "4px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)", marginBottom: "8px" }}>
              {t(locale, "identities.customDomain.txtRecord")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 60px", gap: "8px", fontSize: "11px", fontFamily: "monospace", marginBottom: "8px" }}>
              <div>Type:</div>
              <div style={{ color: "var(--text)" }}>TXT</div>
              <div />
              <div>Name:</div>
              <div style={{ color: "var(--text)" }}>@</div>
              <div />
              <div>Value:</div>
              <div style={{ color: "var(--text)", wordBreak: "break-all" }}>{domainForm.verificationToken}</div>
              <button
                onClick={() => navigator.clipboard.writeText(domainForm.verificationToken)}
                style={{
                  background: "var(--accent)",
                  color: "white",
                  border: "none",
                  padding: "4px 8px",
                  borderRadius: "3px",
                  cursor: "pointer",
                  fontSize: "10px"
                }}
              >
                Copy
              </button>
            </div>
          </div>

          <div style={{ marginBottom: "16px", fontSize: "12px", color: "var(--muted)", lineHeight: "1.5" }}>
            {t(locale, "identities.customDomain.dnsNote")}
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDomainVerified}
              style={{
                background: "var(--accent)",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600
              }}
            >
              {t(locale, "identities.customDomain.checkVerification")}
            </button>
            <button
              onClick={() => setDomainStep("setup")}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--control-border)",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              {t(locale, "identities.customDomain.doLater")}
            </button>
          </div>
        </div>
      )}

      {/* Domain Setup Step */}
      {adding === "custom_domain" && domainStep === "setup" && (
        <div style={{ padding: "16px", border: "1px solid var(--accent)", borderRadius: "6px", marginBottom: "16px", background: "var(--surface)" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "8px" }}>
            {t(locale, "identities.customDomain.verified", { domain: domainForm.domain })}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>
              {t(locale, "identities.customDomain.addAddresses")}
            </label>
            <input
              type="text"
              placeholder="oskar"
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box",
                marginBottom: "8px"
              }}
            />
            <button
              style={{
                background: "transparent",
                border: "1px solid var(--accent)",
                color: "var(--accent)",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: 500
              }}
            >
              {t(locale, "identities.customDomain.addAnother")}
            </button>
          </div>

          <div style={{ marginBottom: "16px", padding: "12px", background: "var(--canvas)", borderRadius: "4px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)", marginBottom: "8px" }}>
              {t(locale, "identities.customDomain.mxRecord")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "70px 1fr 60px", gap: "8px", fontSize: "11px", fontFamily: "monospace" }}>
              <div>Priority: 10</div>
              <div style={{ color: "var(--text)" }}>mail.kveja.com</div>
              <button
                onClick={() => navigator.clipboard.writeText("mail.kveja.com")}
                style={{
                  background: "var(--accent)",
                  color: "white",
                  border: "none",
                  padding: "4px 8px",
                  borderRadius: "3px",
                  cursor: "pointer",
                  fontSize: "10px"
                }}
              >
                Copy
              </button>
              <div>Priority: 20</div>
              <div style={{ color: "var(--text)" }}>mail2.kveja.com</div>
              <button
                onClick={() => navigator.clipboard.writeText("mail2.kveja.com")}
                style={{
                  background: "var(--accent)",
                  color: "white",
                  border: "none",
                  padding: "4px 8px",
                  borderRadius: "3px",
                  cursor: "pointer",
                  fontSize: "10px"
                }}
              >
                Copy
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDomainComplete}
              style={{
                background: "var(--accent)",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600
              }}
            >
              {t(locale, "identities.customDomain.completeSetup")}
            </button>
            <button
              onClick={() => { setAdding(null); setDomainStep(null); }}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--control-border)",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
